import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@teach/ui'
import { api } from '@/store/api'
import { useAppDispatch } from '@/store/hooks'
import {
  idErrorMessage,
  useGetIdProfileQuery,
  useUpdateProfileUsernameMutation,
} from '../profileApi'

import { useMessages } from '@/i18n'
import { messages } from './messages'

const MAX_LENGTH = 32

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: number | undefined
  current: string
}

/** Смена ника. Ник — публичное имя автора, его видят читатели в академии. */
export const ChangeNicknameDialog = ({ open, onOpenChange, userId, current }: Props) => {
  const dispatch = useAppDispatch()
  const { refetch } = useGetIdProfileQuery()
  const [update, { isLoading }] = useUpdateProfileUsernameMutation()
  const [value, setValue] = useState(current)
  const [error, setError] = useState('')
  const m = useMessages(messages)

  useEffect(() => {
    if (open) {
      setValue(current)
      setError('')
    }
  }, [open, current])

  const submit = async () => {
    const nickname = value.trim()
    if (!nickname) return setError(m.enterNickname)
    if (nickname === current) return onOpenChange(false)
    if (userId === undefined) return setError(m.profileUnavailable)
    try {
      await update({ id: userId, profileUsername: nickname }).unwrap()
      await refetch()
      dispatch(api.util.invalidateTags(['Me']))
      toast.success(m.nicknameUpdated)
      onOpenChange(false)
    } catch (e) {
      setError(idErrorMessage(e, m.nicknameUpdateFailed))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{m.nicknameTitle}</DialogTitle>
          <DialogDescription>{m.nicknameHint}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            autoFocus
            value={value}
            maxLength={MAX_LENGTH}
            placeholder={m.enterNickname}
            onChange={(e) => {
              setError('')
              setValue(e.target.value)
            }}
            onKeyDown={(e) => e.key === 'Enter' && void submit()}
          />
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {m.cancel}
          </Button>
          <Button onClick={() => void submit()} disabled={isLoading}>
            {m.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
